import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  List, ListItemButton, ListItemAvatar, ListItemText, Avatar, Checkbox,
  CircularProgress, Alert, Divider, Chip, TextField, InputAdornment, IconButton,
  Tooltip, Collapse,
} from '@mui/material';
import {
  Close, ContentCopy, Link as LinkIcon, Check, People, InfoOutlined,
} from '@mui/icons-material';
import { buddyService, UserPublicProfile } from '../services/buddyService';
import { diveShareService } from '../services/diveShareService';
import { useBuddies } from '../hooks/useBuddies';

interface Props {
  open: boolean;
  onClose: () => void;
  diveId: string;
  diveLabel: string;
  currentUserId: string | null;
  onShared?: (count: number) => void;
}

function initials(name: string) {
  return name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
}

/**
 * Lets the owner of a dive offer it to the buddies they dived with. Objective dive data
 * transfers; the recipient fills in their own air, weights and gear.
 */
const ShareDiveDialog: React.FC<Props> = ({
  open, onClose, diveId, diveLabel, currentUserId, onShared,
}) => {
  const { buddies, loading: buddiesLoading } = useBuddies(open ? currentUserId : null);

  const [profiles, setProfiles] = useState<Record<string, UserPublicProfile>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState('');
  const [sharing, setSharing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shareCode, setShareCode] = useState<string | null>(null);
  const [creatingLink, setCreatingLink] = useState(false);
  const [copied, setCopied] = useState(false);
  const [doneCount, setDoneCount] = useState<number | null>(null);

  // Reset per-open so a reopened dialog never shows the previous dive's result
  useEffect(() => {
    if (open) {
      setSelected(new Set());
      setFilter('');
      setError(null);
      setShareCode(null);
      setCopied(false);
      setDoneCount(null);
    }
  }, [open]);

  const buddyIds = useMemo(
    () => buddies.map(b => b.buddyUserId).filter(Boolean),
    [buddies],
  );

  // Buddy rows only carry ids, so display names come from the profile endpoint
  useEffect(() => {
    if (!open || buddyIds.length === 0) return;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        buddyIds.map(async id => {
          try {
            return [id, await buddyService.getUserProfile(id)] as const;
          } catch {
            return null;
          }
        }),
      );
      if (cancelled) return;
      const next: Record<string, UserPublicProfile> = {};
      for (const entry of entries) if (entry) next[entry[0]] = entry[1];
      setProfiles(next);
    })();
    return () => { cancelled = true; };
  }, [open, buddyIds]);

  const displayName = useCallback(
    (id: string) => profiles[id]?.displayName || 'Loading…',
    [profiles],
  );

  const visibleBuddies = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return buddyIds;
    return buddyIds.filter(id => displayName(id).toLowerCase().includes(q));
  }, [buddyIds, filter, displayName]);

  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const shareUrl = shareCode ? `${window.location.origin}/dive/shared/${shareCode}` : null;

  const handleShare = async () => {
    if (selected.size === 0) return;
    setSharing(true);
    setError(null);
    try {
      const result = await diveShareService.shareDive(diveId, Array.from(selected), false);
      setDoneCount(result.sharedCount);
      onShared?.(result.sharedCount);
      if (result.sharedCount === 0) {
        setError('Those buddies already have this dive shared with them.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to share dive');
    } finally {
      setSharing(false);
    }
  };

  const handleCreateLink = async () => {
    setCreatingLink(true);
    setError(null);
    try {
      const result = await diveShareService.shareDive(diveId, [], true);
      if (result.shareCode) setShareCode(result.shareCode);
      else setError('Could not create a share link');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create share link');
    } finally {
      setCreatingLink(false);
    }
  };

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy — select and copy the link manually.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
            Share this dive
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {diveLabel}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Set expectations up front: gear data deliberately does not travel */}
        <Alert severity="info" icon={<InfoOutlined />} sx={{ mb: 2 }}>
          Your buddy gets the depth profile, times, temperature and location. They fill in their
          own air, weights and gear.
        </Alert>

        {doneCount !== null && doneCount > 0 && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Shared with {doneCount} {doneCount === 1 ? 'buddy' : 'buddies'}. They'll get a
            notification to accept it.
          </Alert>
        )}

        {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

        {buddiesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : buddyIds.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <People sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              No dive buddies yet
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Add buddies to share dives directly, or use a share link below.
            </Typography>
          </Box>
        ) : (
          <>
            {buddyIds.length > 5 && (
              <TextField
                size="small"
                fullWidth
                placeholder="Filter buddies"
                value={filter}
                onChange={e => setFilter(e.target.value)}
                sx={{ mb: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <People fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            <List dense disablePadding sx={{ maxHeight: 260, overflowY: 'auto' }}>
              {visibleBuddies.map(id => (
                <ListItemButton key={id} onClick={() => toggle(id)} dense>
                  <Checkbox
                    edge="start"
                    checked={selected.has(id)}
                    tabIndex={-1}
                    disableRipple
                    size="small"
                  />
                  <ListItemAvatar sx={{ minWidth: 44 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.8rem' }}>
                      {initials(displayName(id)) || '?'}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={displayName(id)}
                    secondary={
                      profiles[id]?.diveCountLabel
                        ? `${profiles[id].diveCountLabel} logged`
                        : undefined
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          </>
        )}

        <Divider sx={{ my: 2 }}>
          <Chip size="small" label="or" />
        </Divider>

        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Share link
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            For a buddy who isn't on New Achievement yet. Anyone with the link can add this dive,
            so only send it to the person you dived with. Expires in 30 days.
          </Typography>

          <Collapse in={!shareCode}>
            <Button
              size="small"
              variant="outlined"
              startIcon={creatingLink ? <CircularProgress size={14} /> : <LinkIcon />}
              onClick={handleCreateLink}
              disabled={creatingLink}
            >
              Create share link
            </Button>
          </Collapse>

          <Collapse in={!!shareCode}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                size="small"
                fullWidth
                value={shareUrl ?? ''}
                InputProps={{ readOnly: true, sx: { fontSize: '0.8rem' } }}
                onFocus={e => e.target.select()}
              />
              <Tooltip title={copied ? 'Copied' : 'Copy link'}>
                <IconButton size="small" onClick={handleCopy} color={copied ? 'success' : 'default'}>
                  {copied ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>
          </Collapse>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          onClick={handleShare}
          disabled={selected.size === 0 || sharing}
          startIcon={sharing ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {selected.size > 0 ? `Share with ${selected.size}` : 'Share'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareDiveDialog;
